import { createReducer } from '@reduxjs/toolkit'
import {
  LoadingStatus,
  Option,
  WithIsOpen,
  Resource,
  ContractorFormValues,
} from 'shared/types'
import { initResource, setFailed, setPending, setSucceeded } from 'shared/utils'
import * as actions from './actions'
import {
  AUTOCOMPLETE_DEFAULT_PAGE,
  AUTOCOMPLETE_DEFAULT_PAGE_SIZE,
} from 'shared/constants/pagination'
import {
  Contractor,
  ContractorDetails,
  ContractorContact,
} from 'shared/types/contractors'

interface ContractorsAutocomplete {
  pagination: {
    page: number
    pageSize: number
    totalRowsCount: number
  }
  selectedContractors: Option<string, number>[]
}

interface AddContractorSuccessDialogState {
  contractor: Contractor | null
}

interface AddContractorDialogState extends WithIsOpen {
  taxNumberField: Resource<ContractorFormValues | undefined>
}

interface ContractorDialogs {
  addContractorSuccessDialogState: AddContractorSuccessDialogState
  addContactDialogState: WithIsOpen
  addContractorDialogState: AddContractorDialogState
}
interface State {
  contractors: Contractor[]
  autocomplete: ContractorsAutocomplete
  contractorDetails: Resource<ContractorDetails | null>
  loadingState: Record<'contractors' | 'addContractorDialog', LoadingStatus>
  contractorContacts: Resource<ContractorContact[]>
  editContractorContact: Resource
  addContractorContact: Resource
  deleteContractorContact: Resource
  getContractorContactStatus: Resource
  dialogs: ContractorDialogs
  addContractorError: boolean
  contractorsCount: number
  areThereNoResults: boolean
  error?: string | null
  editContractor: Resource
  setContractorName: Resource
  setContractorAddress: Resource
}

const initialState: State = {
  contractors: [],
  contractorContacts: initResource(),
  editContractorContact: initResource(),
  addContractorContact: initResource(),
  deleteContractorContact: initResource(),
  getContractorContactStatus: initResource(),
  contractorDetails: initResource(null),
  autocomplete: {
    selectedContractors: [],
    pagination: {
      page: AUTOCOMPLETE_DEFAULT_PAGE,
      pageSize: AUTOCOMPLETE_DEFAULT_PAGE_SIZE,
      totalRowsCount: 0,
    },
  },
  loadingState: {
    contractors: LoadingStatus.Idle,
    addContractorDialog: LoadingStatus.Idle,
  },
  dialogs: {
    addContractorSuccessDialogState: {
      contractor: null,
    },
    addContactDialogState: {
      isOpen: false,
    },
    addContractorDialogState: {
      isOpen: false,
      taxNumberField: initResource(),
    },
  },
  addContractorError: false,
  contractorsCount: 0,
  areThereNoResults: false,
  error: null,
  editContractor: initResource(),
  setContractorName: initResource(),
  setContractorAddress: initResource(),
}

export default createReducer(initialState, builder =>
  builder
    .addCase(actions.getContractors.pending, state => {
      state.loadingState.contractors = LoadingStatus.Pending
      state.error = initialState.error
    })
    .addCase(
      actions.getContractors.fulfilled,
      (
        state,
        {
          payload: {
            response: { results, totalRowsCount },
          },
          meta: { arg: filters },
        }
      ) => {
        state.loadingState.contractors = LoadingStatus.Succeeded
        state.error = null
        state.contractors = [...state.contractors, ...results]
        state.contractorsCount = totalRowsCount
        state.areThereNoResults =
          !(filters?.onlyIncomplete || filters?.search) && !totalRowsCount
      }
    )
    .addCase(actions.getContractors.rejected, (state, action) => {
      state.loadingState.contractors = LoadingStatus.Failed
      state.error = action.error.message
    })
    .addCase(actions.getContractor.pending, state => {
      setPending(state.contractorDetails)
    })
    .addCase(
      actions.getContractor.fulfilled,
      (state, { payload: { response } }) => {
        setSucceeded(state.contractorDetails, response)
        state.contractors = state.contractors.map(contractor =>
          contractor.id === response.id
            ? { ...contractor, contactStatus: response.contactStatus }
            : contractor
        )
      }
    )
    .addCase(actions.getContractor.rejected, (state, action) => {
      setFailed(state.contractorDetails, action.error.message)
    })
    .addCase(actions.getContractorContacts.pending, state => {
      setPending(state.contractorContacts, [])
    })
    .addCase(
      actions.getContractorContacts.fulfilled,
      (state, { payload: { response } }) => {
        setSucceeded(state.contractorContacts, response)
      }
    )
    .addCase(actions.getContractorContacts.rejected, (state, action) => {
      setFailed(state.contractorContacts, action.payload)
    })
    .addCase(actions.editContractorContact.pending, state => {
      setPending(state.editContractorContact)
    })
    .addCase(
      actions.editContractorContact.fulfilled,
      (
        state,
        {
          payload: {
            response: { id, ...response },
          },
        }
      ) => {
        state.contractorContacts.data = state.contractorContacts.data.map(
          contact => (contact.id === id ? { ...contact, ...response } : contact)
        )
        setSucceeded(state.editContractorContact)
      }
    )
    .addCase(actions.editContractorContact.rejected, (state, action) => {
      setFailed(state.editContractorContact, action.payload)
    })
    .addCase(actions.deleteContractorContact.pending, state => {
      setPending(state.deleteContractorContact)
    })
    .addCase(actions.deleteContractorContact.fulfilled, (state, response) => {
      state.contractorContacts.data = state.contractorContacts.data.filter(
        contact => contact.id !== response.payload.response.id
      )
      setSucceeded(state.deleteContractorContact)
    })
    .addCase(actions.deleteContractorContact.rejected, (state, action) => {
      setFailed(state.deleteContractorContact, action.payload)
    })
    .addCase(actions.addContractorContact.pending, state => {
      setPending(state.addContractorContact)
    })
    .addCase(
      actions.addContractorContact.fulfilled,
      (state, { payload: { response } }) => {
        state.contractorContacts.data.push(response)
        setSucceeded(state.addContractorContact)
      }
    )
    .addCase(actions.addContractorContact.rejected, (state, action) => {
      setFailed(state.addContractorContact, action.payload)
    })
    .addCase(actions.addContractor.pending, state => {
      state.loadingState.addContractorDialog = LoadingStatus.Pending
      state.error = initialState.error
    })
    .addCase(
      actions.addContractor.fulfilled,
      (state, { payload: { response } }) => {
        state.loadingState.addContractorDialog = LoadingStatus.Succeeded
        state.dialogs.addContractorDialogState.isOpen = false
        const { id, name, contactStatus, taxNumber } = response
        if (response.openSuccessDialog)
          state.dialogs.addContractorSuccessDialogState.contractor = {
            id,
            name,
            contactStatus,
            taxNumber,
          }
        state.error = null
      }
    )
    .addCase(actions.addContractor.rejected, (state, { payload }) => {
      state.loadingState.addContractorDialog = LoadingStatus.Failed
      state.dialogs.addContractorDialogState.taxNumberField.error =
        payload?.errorCode || payload?.error
      state.addContractorError = true
      state.error = payload?.error
    })
    .addCase(actions.clearContractors, state => {
      state.contractors = []
      state.contractorsCount = 0
    })
    .addCase(actions.getContractorsAutocomplete.pending, state => {
      state.loadingState.contractors = LoadingStatus.Pending
      state.error = initialState.error
    })
    .addCase(
      actions.getContractorsAutocomplete.fulfilled,
      (
        state,
        {
          payload: {
            response: { results, ...pagination },
          },
        }
      ) => {
        state.contractors = [...state.contractors, ...results]
        state.autocomplete.pagination = pagination
        state.loadingState.contractors = LoadingStatus.Succeeded
        state.error = null
      }
    )
    .addCase(actions.getContractorsAutocomplete.rejected, (state, action) => {
      state.contractors = []
      state.loadingState.contractors = LoadingStatus.Failed
      state.error = action.error.message
    })
    .addCase(actions.toggleContractor, (state, { payload }) => {
      state.autocomplete.selectedContractors =
        state.autocomplete.selectedContractors.some(
          contractor => contractor.value === payload.contractor.value
        )
          ? state.autocomplete.selectedContractors.filter(
              contractor => contractor.value !== payload.contractor.value
            )
          : [...state.autocomplete.selectedContractors, payload.contractor]
    })
    .addCase(actions.clearSelectedContractors, state => {
      state.autocomplete.selectedContractors = []
    })
    .addCase(actions.closeAddContractorSuccessDialog, state => {
      state.dialogs.addContractorSuccessDialogState = { contractor: null }
    })
    .addCase(actions.clearAddContractorError, state => {
      state.addContractorError = false
    })
    .addCase(actions.setAddContactDialogOpen, (state, { payload }) => {
      state.dialogs.addContactDialogState.isOpen = payload
    })
    .addCase(actions.setAddContractorDialogOpen, (state, { payload }) => {
      state.dialogs.addContractorDialogState.isOpen = payload
      state.dialogs.addContractorDialogState.taxNumberField.error = undefined
      state.dialogs.addContractorDialogState.taxNumberField.data = undefined
    })
    .addCase(actions.clearTaxNumberError, state => {
      state.dialogs.addContractorDialogState.taxNumberField.error = undefined
    })
    .addCase(
      actions.setAddContractorDialogLoadingStatus,
      (state, { payload }) => {
        state.loadingState.addContractorDialog = payload
      }
    )
    .addCase(actions.fetchContractorByTaxNumber.pending, state => {
      setPending(state.dialogs.addContractorDialogState.taxNumberField)
      state.dialogs.addContractorDialogState.taxNumberField.data = undefined
    })
    .addCase(
      actions.fetchContractorByTaxNumber.fulfilled,
      (state, response) => {
        setSucceeded(state.dialogs.addContractorDialogState.taxNumberField)
        state.dialogs.addContractorDialogState.taxNumberField.data =
          response.payload.response
        state.dialogs.addContractorDialogState.taxNumberField.error = null
      }
    )
    .addCase(
      actions.fetchContractorByTaxNumber.rejected,
      (state, { payload }) => {
        if (Array.isArray(payload))
          setFailed(
            state.dialogs.addContractorDialogState.taxNumberField,
            payload[0].errorCode
          )
        else
          setFailed(
            state.dialogs.addContractorDialogState.taxNumberField,
            payload?.errorCode
          )
      }
    )
    .addCase(actions.getContractorContactStatus.pending, state => {
      setPending(state.getContractorContactStatus)
    })
    .addCase(
      actions.getContractorContactStatus.fulfilled,
      (state, { payload: { response } }) => {
        setSucceeded(state.getContractorContactStatus)
        if (state.contractorDetails.data) {
          state.contractorDetails.data.contactStatus = response.contactStatus
        }
        state.contractors = state.contractors.map(contractor =>
          contractor.id === response.id
            ? { ...contractor, contactStatus: response.contactStatus }
            : contractor
        )
      }
    )
    .addCase(actions.getContractorContactStatus.rejected, state => {
      setFailed(state.getContractorContactStatus)
    })
    .addCase(actions.resetContractorDetailsState, state => {
      state.contractorDetails = initialState.contractorDetails
    })
    .addCase(actions.resetContractorContactsState, state => {
      state.contractorContacts = initialState.contractorContacts
    })
    .addCase(actions.resetDialogsState, state => {
      state.dialogs = initialState.dialogs
    })
    .addCase(actions.editContractor.pending, state => {
      setPending(state.editContractor)
    })
    .addCase(
      actions.editContractor.fulfilled,
      (state, { payload: contractorDetails }) => {
        setSucceeded(state.editContractor)
        if (state.contractorDetails.data) {
          state.contractorDetails.data = {
            ...state.contractorDetails.data,
            ...contractorDetails,
          }
        }
        state.contractors = state.contractors.map(contractor =>
          contractor.id === contractorDetails.id
            ? { ...contractor, name: contractorDetails.name }
            : contractor
        )
      }
    )
    .addCase(actions.editContractor.rejected, state => {
      setFailed(state.editContractor)
    })
    .addCase(actions.setContractorAddress.pending, state => {
      setPending(state.setContractorAddress)
    })
    .addCase(
      actions.setContractorAddress.fulfilled,
      (state, { meta: { arg } }) => {
        setSucceeded(state.setContractorAddress)
        if (state.contractorDetails.data) {
          state.contractorDetails.data = {
            ...state.contractorDetails.data,
            ...arg,
          }
        }
      }
    )
    .addCase(actions.setContractorAddress.rejected, (state, action) => {
      setFailed(state.setContractorAddress, action.payload)
    })
    .addCase(actions.setContractorName.pending, state => {
      setPending(state.setContractorName)
    })
    .addCase(
      actions.setContractorName.fulfilled,
      (
        state,
        {
          meta: {
            arg: { id, name },
          },
        }
      ) => {
        setSucceeded(state.setContractorName)
        if (state.contractorDetails.data) {
          state.contractorDetails.data.name = name
        }
        state.contractors = state.contractors.map(contractor =>
          contractor.id === id ? { ...contractor, name } : contractor
        )
      }
    )
    .addCase(actions.setContractorName.rejected, (state, action) => {
      setFailed(state.setContractorName, action.payload)
    })
)
